import * as moment from 'moment';
import { DataTable, DateRange, DateTrunc, ValueAccessor } from './models';
import { DefaultTimestampAccessor } from './models/Accessor';
import { isNullOrUndefined } from './models/DataTable';
import {
  ConvertibleToSecondDateTrunc,
  ConvertibleToMonthDateTrunc,
  isConvertibleToSecondDateTrunc,
  isConvertibleToMonthDateTrunc,
} from './models/DateGranularity';
import { Duration } from './models/Duration';

// DateRange is fully inclusive, but in the case of relativeDateRanges, we want
// to apply an (exclusive, inclusive] range, so we adjust dateRange.first by
// the smallest unit available
export const rangeRelativeToDate = (date: Date, relativeWindow: Duration): DateRange => {
  const unitForMoment = momentStringFromDateTrunc(relativeWindow.unit);
  const first = adjustDateForwardByOneMillisecond(dateBefore(date, relativeWindow.numberOfUnits, unitForMoment!));

  return { first, last: date };
};

/**
 * This function is conservative in that if the granularites don't match exactly it returns false.
 * For example, days can not be compared to years because leap years have 366 days. Also, days
 * can't be compared to months because months have a variable number of days.
 * @param a first Duration
 * @param b second Duration
 * @returns true if Duration a is equal to Duration b
 */
export const areDurationsEqual = (a: Duration, b: Duration): boolean => {
  if (a.unit === b.unit && a.numberOfUnits === b.numberOfUnits) {
    return true;
  } else if (isConvertibleToSecondDateTrunc(a.unit) && isConvertibleToSecondDateTrunc(b.unit)) {
    return getUnitsInSeconds(a.unit, a.numberOfUnits) === getUnitsInSeconds(b.unit, b.numberOfUnits);
  } else if (isConvertibleToMonthDateTrunc(a.unit) && isConvertibleToMonthDateTrunc(b.unit)) {
    return getUnitsInMonths(a.unit, a.numberOfUnits) === getUnitsInMonths(b.unit, b.numberOfUnits);
  }

  return false;
};

export const getUnitsInSeconds = (dateTrunc: ConvertibleToSecondDateTrunc, numberOfUnits: number): number => {
  const MINUTE_FACTOR = 60;
  const HOUR_FACTOR = 3_600;
  const DAY_FACTOR = 86_400;
  const WEEK_FACTOR = 604_800;

  if (dateTrunc === DateTrunc.MINUTE) {
    return numberOfUnits * MINUTE_FACTOR;
  } else if (dateTrunc === DateTrunc.HOUR) {
    return numberOfUnits * HOUR_FACTOR;
  } else if (dateTrunc === DateTrunc.DAY) {
    return numberOfUnits * DAY_FACTOR;
  } else if (dateTrunc === DateTrunc.WEEK) {
    return numberOfUnits * WEEK_FACTOR;
  }

  return numberOfUnits;
};

export const getUnitsInMinutes = (
  dateTrunc: DateTrunc.HOUR | DateTrunc.DAY | DateTrunc.WEEK,
  numberOfUnits: number
): number => {
  const HOUR_FACTOR = 60;
  const DAY_FACTOR = 1_440;
  const WEEK_FACTOR = 10_080;

  if (dateTrunc === DateTrunc.HOUR) {
    return numberOfUnits * HOUR_FACTOR;
  } else if (dateTrunc === DateTrunc.DAY) {
    return numberOfUnits * DAY_FACTOR;
  } else if (dateTrunc === DateTrunc.WEEK) {
    return numberOfUnits * WEEK_FACTOR;
  }

  return numberOfUnits;
};

export const getUnitsinHours = (dateTrunc: DateTrunc.DAY | DateTrunc.WEEK, numberOfUnits: number): number => {
  const DAY_FACTOR = 24;
  const WEEK_FACTOR = 168;

  if (dateTrunc === DateTrunc.DAY) {
    return numberOfUnits * DAY_FACTOR;
  } else if (dateTrunc === DateTrunc.WEEK) {
    return numberOfUnits * WEEK_FACTOR;
  }

  return numberOfUnits;
};

const getUnitsInMonths = (dateTrunc: ConvertibleToMonthDateTrunc, numberOfUnits: number): number => {
  const QUARTER_FACTOR = 3;
  const YEAR_FACTOR = 12;

  if (dateTrunc === DateTrunc.QUARTER) {
    return numberOfUnits * QUARTER_FACTOR;
  } else if (dateTrunc === DateTrunc.YEAR) {
    return numberOfUnits * YEAR_FACTOR;
  }

  return numberOfUnits;
};

const adjustDateForwardByOneMillisecond = (date: Date): Date => moment(date).add(1, 'ms').toDate();

export type DateUnit = moment.unitOfTime.DurationConstructor;

export const dateBefore = (date: Date, amount: number, unit: DateUnit): Date => {
  return moment(date).subtract(amount, unit).toDate();
};

export const dateAfter = (date: Date, amount: number, unit: DateUnit): Date => {
  return moment(date).add(amount, unit).toDate();
};

export const withinRange = (range: DateRange, date: Date): boolean => date >= range.first && date <= range.last;

export const momentStringFromDateTrunc = (dateTrunc: DateTrunc): DateUnit | undefined => {
  switch (dateTrunc) {
    case DateTrunc.SECOND: {
      return 'seconds';
    }
    case DateTrunc.MINUTE: {
      return 'minutes';
    }
    case DateTrunc.HOUR: {
      return 'hours';
    }
    case DateTrunc.ISO_WEEKDAY:
    case DateTrunc.DAY: {
      return 'days';
    }
    case DateTrunc.MONTH: {
      return 'months';
    }
    case DateTrunc.ISO_WEEK:
    case DateTrunc.WEEK: {
      return 'weeks';
    }
    case DateTrunc.ISO_QTR:
    case DateTrunc.QUARTER: {
      return 'quarters';
    }
    case DateTrunc.ISO_YEAR:
    case DateTrunc.YEAR: {
      return 'years';
    }
    default: {
      // compare to previous point
      return undefined;
    }
  }
};

export const getDateRange = (
  table: DataTable,
  accessor: ValueAccessor<Date | undefined> = DefaultTimestampAccessor
): DateRange | undefined => {
  if (table.length === 0) {
    return undefined;
  }
  const firstPoint = table[0];
  const lastPoint = table[table.length - 1];

  const first = accessor(firstPoint);
  const last = accessor(lastPoint);
  if (isNullOrUndefined(first) || isNullOrUndefined(last)) {
    return undefined;
  }

  return { first, last };
};

export const areRangesEqual = (a: DateRange, b: DateRange): boolean => {
  return a.first.getTime() === b.first.getTime() && a.last.getTime() === b.last.getTime();
};
